/**
 * 本地选手 4x4 棋盘操作、滑动合并结算与盘面广播控制器 (Local Board Gameplay Controller)
 * 职责：
 * 1. 处理本地玩家四向滑动输入、85ms 动画节流结算与随机新方块生成
 * 2. 结算合并得分、DMC 华丽度加分、5s 待援队友自动 +2 空投触发与 n>=64 定点斩半重炮触发
 * 3. 检查 2048 竞速达成或无限死斗里程碑及封盘终局判定
 * 4. 向全房广播 sync_board 状态并同步给本地托管的 AI 机器人
 */

import type { MoveDirection, PKGamePacket } from "../../shared/protocol";
import {
  processLine,
  getRandomEmptySpot,
  evaluateDangerState,
  isBoardStuck,
  findLastNativeTile,
  getThemeByMaxTile,
  Tile,
} from "./boardEngine";
import type { ArenaDomRefs } from "../ui/domRefs";
import type { ArenaRuntimeState } from "../state/arenaState";
import type { P2PChannel } from "../network/p2pChannel";
import type { SoundEngine } from "./soundEngine";
import type { DmcRankManager } from "./dmcRankManager";
import type { LocalTileRenderer } from "../ui/pkRenderer";
import type { BattleEffects } from "../ui/battleEffects";
import type { BattlefieldView } from "./battlefieldView";
import type { CombatSystem } from "./combatSystem";
import type { GameoverController } from "../ui/gameoverController";

export class LocalBoardController {
  private pendingMoveTimer: number | null = null;
  private pendingMoveSettle: (() => void) | null = null;

  constructor(
    private readonly dom: ArenaDomRefs,
    private readonly state: ArenaRuntimeState,
    private readonly p2p: P2PChannel,
    private readonly sounds: SoundEngine,
    private readonly dmc: DmcRankManager,
    private readonly localRenderer: LocalTileRenderer,
    private readonly effects: BattleEffects,
    private readonly view: BattlefieldView,
    private readonly combat: CombatSystem,
    private readonly gameoverCtrl: GameoverController
  ) {}

  public addRandomTile(): Tile | null {
    const spot = getRandomEmptySpot(this.state.localTiles);
    if (!spot) return null;

    for (const t of this.state.localTiles) {
      t.isLastNative = false;
    }

    const val = Math.random() < 0.9 ? 2 : 4;
    const tile: Tile = {
      id: this.state.nextTileId++,
      r: spot.r,
      c: spot.c,
      val,
      isNew: true,
      isLastNative: true,
    };

    this.state.localTiles.push(tile);
    this.localRenderer.mountTile(tile);
    return tile;
  }

  public flushPendingMoveAnimation() {
    if (this.pendingMoveTimer !== null) {
      clearTimeout(this.pendingMoveTimer);
      this.pendingMoveTimer = null;
    }
    if (this.pendingMoveSettle) {
      const settle = this.pendingMoveSettle;
      this.pendingMoveSettle = null;
      settle();
    }
  }

  public handlePlayerMove(direction: MoveDirection) {
    if (
      !this.state.isMatchRunning ||
      this.state.hasFinished ||
      this.state.isBoardLocked ||
      this.state.myTeam === "spectator"
    ) {
      return;
    }

    this.flushPendingMoveAnimation();
    this.localRenderer.updateCoordsCache();

    const isVertical = direction === "UP" || direction === "DOWN";
    const isReverse = direction === "DOWN" || direction === "RIGHT";
    let boardMoved = false;
    let earned = 0;
    const allRemoved: Tile[] = [];
    const allSpawn: Tile[] = [];
    const allMergedVals: number[] = [];

    for (let i = 0; i < 4; i++) {
      const line = this.state.localTiles.filter((t) => (isVertical ? t.c === i : t.r === i));
      const res = processLine(
        line,
        isReverse,
        isVertical,
        i,
        () => this.state.nextTileId++,
        (t) => {
          this.localRenderer.updateTilePosition(t);
        }
      );

      if (res.moved) {
        boardMoved = true;
        earned += res.earnedScore;
        allRemoved.push(...res.toRemove);
        allSpawn.push(...res.toSpawn);
        allMergedVals.push(...res.mergedValues);
      }
    }

    if (!boardMoved) {
      this.sounds.playBump();
      return;
    }

    const myRuntime = this.state.participants.get(this.state.myPeerId);
    if (myRuntime) {
      myRuntime.lastMoveAt = Date.now();
    }

    this.sounds.playSlide();
    this.state.localMoves++;
    this.state.localScore += earned;

    this.pendingMoveSettle = () => {
      for (const t of allRemoved) {
        this.localRenderer.removeTile(t);
      }
      const removeSet = new Set(allRemoved.map((t) => t.id));
      this.state.localTiles = this.state.localTiles.filter((t) => !removeSet.has(t.id));

      for (const t of allSpawn) {
        this.state.localTiles.push(t);
        this.localRenderer.mountTile(t);
        this.state.localMerges++;
      }

      for (const mv of allMergedVals) {
        this.sounds.playMerge(mv);
        this.dmc.addMergeScore(mv);
        if (mv > this.state.localMaxTile) {
          this.state.localMaxTile = mv;
        }
      }

      // 检查是否触发合成 n >= 64 的定点斩半重炮
      const topMerged = allMergedVals.length > 0 ? Math.max(...allMergedVals) : 0;
      if (topMerged >= 64) {
        this.state.attackGauge = 100;
        this.combat.triggerSniperAttack(topMerged);
      } else if (topMerged > 0) {
        const stepProgress = Math.min(95, Math.round(((Math.log2(topMerged) - 1) / 5) * 100));
        this.state.attackGauge = Math.max(this.state.attackGauge, stepProgress);
      }

      this.addRandomTile();

      this.dom.elLocalBoardWrapper.setAttribute(
        "data-theme",
        getThemeByMaxTile(this.state.localMaxTile)
      );
      const danger = evaluateDangerState(this.state.localTiles);
      this.dom.elLocalBoardWrapper.classList.toggle("in-danger", danger.isCritical);
      if (danger.isCritical) {
        this.sounds.playDangerHeartbeat();
      }

      this.view.updateLocalStatsUI();
      this.checkPostMoveEndConditions();
    };

    this.pendingMoveTimer = window.setTimeout(() => {
      this.pendingMoveTimer = null;
      if (this.pendingMoveSettle) {
        const settle = this.pendingMoveSettle;
        this.pendingMoveSettle = null;
        settle();
      }
    }, 85);
  }

  /**
   * 无论何种原因（滑动生成、接收队友抛射块、受敌方斩击）导致本棋盘触发封盘：
   * 1. 将本棋盘上最后生成的原生方块（排除队友发射过来的 isTransferred 方块）状态变为「待发射 (isPendingEject = true)」
   * 2. 点亮待发射特效并广播状态，一旦有队友存在空位则立刻发射该方块并解封本棋盘
   */
  public enterLocalStuckPendingEject() {
    if (this.state.myTeam === "spectator" || this.state.hasFinished) return;

    for (const t of this.state.localTiles) {
      this.localRenderer.setTilePendingEject(t, false);
    }
    const lastNative = findLastNativeTile(this.state.localTiles);
    if (lastNative) {
      this.localRenderer.setTilePendingEject(lastNative, true);
    }

    this.state.isBoardLocked = true;
    this.sounds.playBump();
    this.dom.elLocalRescueStatus.textContent = lastNative
      ? "封盘 · 待发射"
      : "已封盘定格";
    const me = this.state.participants.get(this.state.myPeerId);
    if (me) {
      me.isStuck = true;
    }

    this.broadcastBoardState();
    this.p2p.sendPacket({
      type: "game_over",
      playerId: this.state.myPeerId,
      playerName: this.state.myName,
      finalScore: this.state.localScore,
      maxTile: this.state.localMaxTile,
      duration: 0,
      cause: "STUCK",
    });

    if (lastNative && (this.state.myTeam === "red" || this.state.myTeam === "blue")) {
      this.combat.scheduleOrFlushPendingEjects(this.state.myTeam);
    }
    this.gameoverCtrl.evaluateTeamStuckConditions();
  }

  /**
   * 当存活队友出现空位时，拔起并消耗本地处于「待发射」状态的最后原生方块，同时解除本地封盘锁定
   */
  public consumeLocalPendingEjectTile(): Tile | null {
    if (
      !this.state.isBoardLocked ||
      this.state.myTeam === "spectator" ||
      this.state.hasFinished
    ) {
      return null;
    }

    const pendingTile = this.state.localTiles.find((t) => Boolean(t.isPendingEject));
    if (!pendingTile) return null;

    this.localRenderer.removeTile(pendingTile);
    this.state.localTiles = this.state.localTiles.filter((t) => t.id !== pendingTile.id);
    for (const t of this.state.localTiles) {
      this.localRenderer.setTilePendingEject(t, false);
    }

    this.state.isBoardLocked = false;
    this.state.localMaxTile = this.state.localTiles.reduce(
      (max, t) => Math.max(max, t.val),
      2
    );
    const me = this.state.participants.get(this.state.myPeerId);
    if (me) {
      me.isStuck = false;
    }

    this.dom.elLocalRescueStatus.textContent = "攻击蓄能";
    const danger = evaluateDangerState(this.state.localTiles);
    this.dom.elLocalBoardWrapper.classList.toggle("in-danger", danger.isCritical);
    this.view.updateLocalStatsUI();
    this.broadcastBoardState();
    this.gameoverCtrl.evaluateTeamStuckConditions();
    return pendingTile;
  }

  public checkPostMoveEndConditions() {
    if (
      !this.state.isMatchRunning ||
      this.state.hasFinished ||
      this.state.myTeam === "spectator"
    ) {
      return;
    }

    if (this.state.localMaxTile >= 2048) {
      if (this.state.gameMode !== "endless") {
        this.broadcastBoardState();
        this.gameoverCtrl.clearTimers();
        this.gameoverCtrl.triggerGameOver(
          true,
          `合出 2048 绝杀！你率领${this.state.myTeam === "red" ? "红方" : "蓝方"}夺冠`
        );
        this.p2p.sendPacket({
          type: "game_over",
          playerId: this.state.myPeerId,
          playerName: this.state.myName,
          winningTeam: this.state.myTeam,
          finalScore: this.state.localScore,
          maxTile: this.state.localMaxTile,
          duration: 0,
          cause: "REACHED_2048",
        });
        return;
      } else if (!this.state.announcedMilestones.has(this.state.localMaxTile)) {
        this.state.announcedMilestones.add(this.state.localMaxTile);
        this.sounds.playVictory();
        this.effects.fireVictoryConfetti();
        this.view.logCombat(
          `突破极限 · 合出 ${this.state.localMaxTile} 传奇块！`
        );
      }
    }

    if (isBoardStuck(this.state.localTiles)) {
      this.enterLocalStuckPendingEject();
    } else {
      this.broadcastBoardState();
      if (
        this.state.localTiles.length < 16 &&
        (this.state.myTeam === "red" || this.state.myTeam === "blue")
      ) {
        this.combat.scheduleOrFlushPendingEjects(this.state.myTeam);
      }
      // 若敌方已封盘处于 60s 追分倒计时，我方每次合成分数上涨后立即检测是否已成功反超
      this.gameoverCtrl.evaluateTeamStuckConditions();
    }
  }

  public broadcastBoardState() {
    if (this.state.myTeam === "spectator") return;

    const tileData = this.state.localTiles.map((t) => ({
      id: t.id,
      r: t.r,
      c: t.c,
      val: t.val,
      isNew: t.isNew,
      isMerged: t.isMerged,
      mergedIds: t.mergedIds,
      isLastNative: t.isLastNative,
      isTransferred: t.isTransferred,
      isPendingEject: t.isPendingEject,
    }));

    for (const t of this.state.localTiles) {
      t.isNew = false;
      t.isMerged = false;
      t.mergedIds = undefined;
    }

    const me = this.state.participants.get(this.state.myPeerId);
    if (me) {
      me.score = this.state.localScore;
      me.maxTile = this.state.localMaxTile;
      me.styleGrade = this.dmc.grade;
      me.tiles = tileData;
      me.isStuck = this.state.isBoardLocked;
    }

    const packet: PKGamePacket = {
      type: "sync_board",
      playerId: this.state.myPeerId,
      playerName: this.state.myName,
      team: this.state.myTeam,
      tiles: tileData,
      score: this.state.localScore,
      maxTile: this.state.localMaxTile,
      moves: this.state.localMoves,
      merges: this.state.localMerges,
      styleGrade: this.dmc.grade,
      styleGauge: this.dmc.gauge,
      inDanger: this.state.localTiles.length >= 14,
      attackGauge: this.state.attackGauge,
      isStuck: this.state.isBoardLocked,
      gameMode: this.state.gameMode,
    };

    for (const bot of this.state.activeBots.values()) {
      if (bot.team !== this.state.myTeam) {
        bot.syncPlayerTelemetry(this.state.localScore, this.state.localMaxTile, tileData);
      }
    }

    this.p2p.sendPacket(packet);
  }
}

